/** @forge-header
 * @id          nflParsers
 * @role        NFL ticker parsers | game, player props, super bowl
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,TickerCategory | kalshiTicker/helpers:nflTeamFromCode,parseFallback,parsePlayerFromMarket
 * @downstream  kalshiTicker/parse
 * @exports     parseNflGame:fn | parseNfl2Td:fn | parseNflFirstTd:fn | parseSuperBowl:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, TickerCategory } from '../types';
import { parseDateFromTicker, formatDateShort } from '../types';
import { parseFallback, nflTeamFromCode, parsePlayerFromMarket } from '../helpers';

/** NFL 2+ touchdowns prop: KXNFL2TD-26FEB08SEANE-SEAABARNER88 */
export function parseNfl2Td(raw: string): ParsedTicker {
  return parseNflPlayerProp(raw, 'KXNFL2TD', 'nfl-2td', '2+ TD');
}

/** NFL first touchdown prop: KXNFLFIRSTTD-26FEB08SEANE-NEKBOUTTE9 */
export function parseNflFirstTd(raw: string): ParsedTicker {
  return parseNflPlayerProp(raw, 'KXNFLFIRSTTD', 'nfl-first-td', 'First TD');
}

/** Common parser for NFL player prop tickers */
function parseNflPlayerProp(
  raw: string,
  series: string,
  category: TickerCategory,
  propLabel: string
): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 12) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamsStr = mid.slice(7);

  let awayCode: string;
  let homeCode: string;

  if (teamsStr.length >= 6) {
    awayCode = teamsStr.slice(0, 3);
    homeCode = teamsStr.slice(3, 6);
  } else if (teamsStr.length >= 5) {
    awayCode = teamsStr.slice(0, 3);
    homeCode = teamsStr.slice(3, 5);
  } else {
    awayCode = teamsStr.slice(0, 3);
    homeCode = teamsStr.slice(3);
  }

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: nflTeamFromCode(awayCode) ?? undefined,
    homeName: nflTeamFromCode(homeCode) ?? undefined,
  };
  const marketPart = parts[2] ?? '';
  const player = parsePlayerFromMarket(marketPart);

  const dateLabel = date ? formatDateShort(date) : '';
  const gameLabel = `${awayCode} @ ${homeCode}`;
  const playerLabel = player?.displayName ?? player?.playerId ?? '';

  const shortLabel = playerLabel ? `${propLabel}: ${playerLabel}` : `NFL ${propLabel}`;

  const fullLabel = [`NFL ${propLabel}`, dateLabel, gameLabel, playerLabel]
    .filter(Boolean)
    .join(' · ');

  return {
    raw,
    category,
    series,
    date,
    game,
    player,
    spread: null,
    total: null,
    tennis: null,
    outcome: marketPart,
    shortLabel,
    fullLabel,
  };
}

/** Super Bowl winner: KXSB-26-SEA */
export function parseSuperBowl(raw: string): ParsedTicker {
  const parts = raw.split('-');
  const year = parts[1] ? `20${parts[1]}` : null;
  const teamCode = parts[2] ?? null;

  const teamName = teamCode ? nflTeamFromCode(teamCode) : null;

  const shortLabel = teamCode ? `SB: ${teamName ?? teamCode}` : 'Super Bowl';

  const fullLabel = year
    ? `Super Bowl ${year}${teamCode ? ` · ${teamName ?? teamCode}` : ''}`
    : shortLabel;

  return {
    raw,
    category: 'nfl-superbowl',
    series: 'KXSB',
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

/** NFL game moneyline: KXNFLGAME-26FEB08SEANE-SEA */
export function parseNflGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 12) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamsStr = mid.slice(7);

  const awayCode = teamsStr.slice(0, 3);
  const homeCode = teamsStr.slice(3, 5) || teamsStr.slice(3, 6);
  const teamCode = parts[2] ?? null;

  const awayName = nflTeamFromCode(awayCode);
  const homeName = nflTeamFromCode(homeCode);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: awayName ?? undefined,
    homeName: homeName ?? undefined,
  };

  const shortLabel = `NFL: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NFL: ${awayName ?? awayCode} @ ${homeName ?? homeCode} · ${teamCode} Win`
    : `NFL: ${awayName ?? awayCode} @ ${homeName ?? homeCode}`;

  return {
    raw,
    category: 'nfl-game',
    series: 'KXNFLGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
